import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { FiPlus, FiEdit2, FiSave } from "react-icons/fi";
import useAsync from "../../hooks/useAsync";
import { getMasters, saveMaster, getCompanySettings, updateCompanySettings } from "../../services/settingsApi";
import { getEmployees } from "../../services/employeeApi";
import { Card, PageHeader, Button, DataTable, StatusBadge, Modal, FormField, Tabs, ErrorState, Loader, Banner, ConfirmDialog } from "../../components/common";
import { getErrorMessage } from "../../utils/errors";
import Select from "../../components/common/Select"; // [NEW] alphabetical dropdown (replaces <select>)

const api = { getMasters, saveMaster, getCompanySettings, updateCompanySettings, getEmployees };

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function MasterTab({ kind, label }) {
  const list = useAsync(() => api.getMasters(kind), [kind]);
  const people = useAsync(() => api.getEmployees({}).catch(() => ({ data: null })), []);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [deactivating, setDeactivating] = useState(null);
  const usersOf = (row) => (people.data || []).filter((e) => e.status === "Active" && String(kind === "department" ? e.departmentId : e.designationId) === String(row.id));

  const save = async (row) => {
    setBusy(true);
    try {
      await api.saveMaster(kind, row);
      toast.success(`${label} saved.`);
      setEditing(null);
      list.reload();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (list.error) return <ErrorState message={list.error} onRetry={list.reload} />;

  return (
    <Card
      padded={false}
      title={`${label}s`}
      actions={<Button size="sm" icon={FiPlus} onClick={() => setEditing({ code: "", name: "", active: true })}>Add {label.toLowerCase()}</Button>}
    >
      <DataTable
        rows={list.data || []}
        loading={list.loading && !list.data}
        emptyTitle={`No ${label.toLowerCase()}s yet`}
        columns={[
          { key: "name", label, render: (r) => <span className="cell-stack"><strong>{r.name}</strong><small>{r.code}</small></span> },
          { key: "active", label: "Status", render: (r) => <StatusBadge status={r.active ? "Active" : "Inactive"} /> },
          { key: "people", label: "Active employees", align: "right", render: (r) => (people.data ? usersOf(r).length : "…") },
          {
            key: "act", label: "", align: "right",
            render: (r) => (
              <span className="row-actions">
                <Button size="sm" variant="ghost" icon={FiEdit2} aria-label={`Edit ${r.name}`} onClick={() => setEditing({ ...r })} />
                <Button size="sm" variant="ghost" onClick={() => (r.active ? setDeactivating(r) : save({ ...r, active: true, reason: `${label} activated` }))}>
                  {r.active ? "Deactivate" : "Activate"}
                </Button>
              </span>
            ),
          },
        ]}
      />
      <ConfirmDialog
        open={Boolean(deactivating)}
        title={`Deactivate ${deactivating?.name}?`}
        message={deactivating && usersOf(deactivating).length
          ? `${usersOf(deactivating).length} active employee(s) still belong to this ${label.toLowerCase()}. Move them to another ${label.toLowerCase()} first (Employees page); otherwise their profile would show a blank ${label.toLowerCase()} and they can't be edited.`
          : `It can no longer be chosen for employees. Nobody active uses it right now.`}
        confirmLabel="Deactivate"
        tone="danger"
        loading={busy}
        onConfirm={async () => {
          if (usersOf(deactivating).length) { toast.error(`Move the ${usersOf(deactivating).length} employee(s) first.`); return; }
          await save({ ...deactivating, active: false, reason: `${label} deactivated` });
          setDeactivating(null);
        }}
        onClose={() => setDeactivating(null)}
      >
        {deactivating && usersOf(deactivating).length > 0 && (
          <p className="muted">{usersOf(deactivating).slice(0, 10).map((e) => e.name).join(", ")}{usersOf(deactivating).length > 10 ? "…" : ""}</p>
        )}
      </ConfirmDialog>
      <Modal
        open={Boolean(editing)}
        size="sm"
        title={editing?.id ? `Edit ${editing.name}` : `Add ${label.toLowerCase()}`}
        onClose={busy ? undefined : () => setEditing(null)}
        footer={<><Button variant="ghost" onClick={() => setEditing(null)} disabled={busy}>Cancel</Button><Button onClick={() => save(editing)} loading={busy}>Save</Button></>}
      >
        {editing && (
          <div className="form-stack">
            <FormField label="Code" required hint="Letters, digits, _ and -">
              <input value={editing.code} onChange={(e) => setEditing({ ...editing, code: e.target.value.replace(/[^A-Za-z0-9_-]/g, "") })} />
            </FormField>
            <FormField label="Name" required>
              <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
            </FormField>
          </div>
        )}
      </Modal>
    </Card>
  );
}

function CompanyTab() {
  const settings = useAsync(() => api.getCompanySettings(), []);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (settings.data !== null || settings.loading) {
      setForm(settings.data || { companyName: "Teliolabs", timezone: "Asia/Kolkata", financialYearStartMonth: 4, locale: "en-IN", retentionPolicy: {} });
    }
  }, [settings.data, settings.loading]);

  if (settings.error) return <ErrorState message={settings.error} onRetry={settings.reload} />;
  if (!form) return <Loader />;

  const save = async () => {
    setBusy(true);
    try {
      await api.updateCompanySettings(form);
      toast.success("Company settings saved.");
      settings.reload();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Company settings" actions={<Button icon={FiSave} loading={busy} onClick={save}>Save</Button>}>
      {!settings.data && <Banner tone="info" title="Not set yet">Save once to store the company settings.</Banner>}
      <div className="form-grid">
        <FormField label="Company name"><input value={form.companyName} onChange={(e) => setForm({ ...form, companyName: e.target.value })} /></FormField>
        <FormField label="Business timezone" hint="IANA name, e.g. Asia/Kolkata"><input value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} /></FormField>
        <FormField label="Financial year starts in">
          <Select keepOrder value={form.financialYearStartMonth} onChange={(e) => setForm({ ...form, financialYearStartMonth: Number(e.target.value) })}>
            {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </Select>
        </FormField>
        <FormField label="Default locale"><input value={form.locale} onChange={(e) => setForm({ ...form, locale: e.target.value })} /></FormField>
        <FormField label="Record retention (years)" hint="Stored in the retention policy.">
          <input type="number" min="1" value={form.retentionPolicy?.years ?? ""} onChange={(e) => setForm({ ...form, retentionPolicy: { ...form.retentionPolicy, years: e.target.value ? Number(e.target.value) : undefined } })} />
        </FormField>
      </div>
    </Card>
  );
}

export default function OrganisationSetupPage() {
  const [tab, setTab] = useState("department");
  return (
    <div className="page">
      <PageHeader title="Organisation setup" subtitle="Departments, designations and company-wide settings used across the PMS." />
      <Tabs
        value={tab}
        onChange={setTab}
        tabs={[
          { key: "department", label: "Departments" },
          { key: "designation", label: "Designations" },
          { key: "company", label: "Company settings" },
        ]}
      />
      {tab === "department" && <MasterTab kind="department" label="Department" />}
      {tab === "designation" && <MasterTab kind="designation" label="Designation" />}
      {tab === "company" && <CompanyTab />}
    </div>
  );
}
